import React, { useContext, useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { ToastContext } from '../../../services/contexts/toast-context';
import { usePatchProject } from '../../../services/api/projects-api';
import { useGetFullPortfolio } from '../../../services/api/users-api';
import Flex from '../../../components/shared-styles/flexi';

// ==================== Edit Overview (title + description) ====================

export function EditOverviewDialog({ visible, onHide, project }) {
  const toastRef = useContext(ToastContext);
  const { mutateAsync: patchProject, isPending } = usePatchProject();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (project) {
      setTitle(project.title || '');
      setDescription(project.description || '');
    }
  }, [project, visible]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toastRef.current.show({ severity: 'warn', summary: 'Validation Error', detail: 'Project title is required', life: 3000 });
      return;
    }

    try {
      await patchProject({ projectId: project.id, data: { title, description } });
      toastRef.current.show({ severity: 'success', summary: 'Success', detail: 'Project overview updated', life: 3000 });
      onHide();
    } catch (error) {
      toastRef.current.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to update project', life: 3000 });
    }
  };

  return (
    <Dialog visible={visible} onHide={onHide} header="Edit Overview" modal style={{ width: '560px', maxWidth: '95vw' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div>
          <label htmlFor="overview-title" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Title *</label>
          <InputText
            id="overview-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter project title"
            className="w-full"
          />
        </div>

        <div>
          <label htmlFor="overview-description" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Description</label>
          <InputTextarea
            id="overview-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Detailed description of the project"
            rows={6}
            className="w-full"
          />
        </div>

        <Flex gap="1rem" justify="flex-end">
          <Button label="Cancel" className="p-button-text p-button-secondary" onClick={onHide} />
          <Button label="Save Changes" className="p-button-primary" loading={isPending} onClick={handleSubmit} />
        </Flex>
      </div>
    </Dialog>
  );
}

// ==================== Add Skill ====================

export function AddSkillDialog({ visible, onHide, project }) {
  const toastRef = useContext(ToastContext);
  const { mutateAsync: patchProject, isPending } = usePatchProject();
  const { data: portfolioData, isLoading: isLoadingPortfolio } = useGetFullPortfolio();
  const skillsData = portfolioData?.skills || [];

  const [selectedSkillIds, setSelectedSkillIds] = useState([]);
  const [newSkills, setNewSkills] = useState([]);
  const [newSkillInput, setNewSkillInput] = useState('');

  useEffect(() => {
    if (visible) {
      setSelectedSkillIds([]);
      setNewSkills([]);
      setNewSkillInput('');
    }
  }, [visible]);

  const existingSkillIds = project?.skills?.map((s) => s.id) || [];
  // Only filter out skills already in the project
  const availableSkills = skillsData.filter((skill) => !existingSkillIds.includes(skill.id));
  const skillOptions = availableSkills.map((skill) => ({ label: skill.name, value: skill.id }));

  const handleAddPendingSkill = () => {
    const skills = newSkillInput.split(',').map((s) => s.trim()).filter(Boolean);
    if (skills.length > 0) {
      setNewSkills([...new Set([...newSkills, ...skills])]);
      setNewSkillInput('');
    }
  };

  const handleSubmit = async () => {
    if (selectedSkillIds.length === 0 && newSkills.length === 0) {
      toastRef.current.show({ severity: 'warn', summary: 'Nothing to add', detail: 'Select or type at least one skill', life: 3000 });
      return;
    }

    try {
      await patchProject({
        projectId: project.id,
        data: {
          skill_ids: [...existingSkillIds, ...selectedSkillIds],
          new_skills: newSkills,
        },
      });
      toastRef.current.show({ severity: 'success', summary: 'Success', detail: 'Skills updated', life: 3000 });
      onHide();
    } catch (error) {
      toastRef.current.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to add skills', life: 3000 });
    }
  };

  return (
    <Dialog visible={visible} onHide={onHide} header="Add Skill" modal style={{ width: '520px', maxWidth: '95vw' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div>
          <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
            Existing skills {availableSkills.length === 0 && <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>(all skills already added)</span>}
          </label>
          <MultiSelect
            value={selectedSkillIds}
            onChange={(e) => setSelectedSkillIds(e.value)}
            options={skillOptions}
            placeholder={isLoadingPortfolio ? "Loading skills..." : "Select skills to add"}
            maxSelectedLabels={5}
            className="w-full"
            loading={isLoadingPortfolio}
            disabled={isLoadingPortfolio || skillOptions.length === 0}
          />
        </div>

        <div>
          <label style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', display: 'block' }}>
            Or add new skills (separated by commas)
          </label>
          <Flex gap="0.5rem">
            <InputText
              value={newSkillInput}
              onChange={(e) => setNewSkillInput(e.target.value)}
              placeholder="e.g., React, Python, Docker"
              style={{ flex: 1 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddPendingSkill();
                }
              }}
            />
            <Button
              icon="pi pi-plus"
              onClick={handleAddPendingSkill}
              className="p-button-sm p-button-outlined"
              disabled={!newSkillInput.trim()}
              tooltip="Add skill"
            />
          </Flex>

          {newSkills.length > 0 && (
            <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {newSkills.map((skill, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.25rem 0.5rem',
                    background: '#e0f2fe',
                    color: '#0369a1',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                  }}
                >
                  <span>{skill}</span>
                  <i
                    className="pi pi-times"
                    style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    onClick={() => setNewSkills(newSkills.filter((_, i) => i !== index))}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <Flex gap="1rem" justify="flex-end">
          <Button label="Cancel" className="p-button-text p-button-secondary" onClick={onHide} />
          <Button label="Add Skill" className="p-button-primary" loading={isPending} onClick={handleSubmit} />
        </Flex>
      </div>
    </Dialog>
  );
}

export function AddLinkDialog({ visible, onHide, project }) {
  const toastRef = useContext(ToastContext);
  const { mutateAsync: patchProject, isPending } = usePatchProject();
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (visible) {
      setTitle('');
      setUrl('');
    }
  }, [visible]);

  const handleSubmit = async () => {
    if (!title.trim() || !url.trim()) {
      toastRef.current.show({ severity: 'warn', summary: 'Validation Error', detail: 'Link title and URL are required', life: 3000 });
      return;
    }

    const existingLinks = Array.isArray(project?.links) ? project.links : [];

    try {
      await patchProject({ projectId: project.id, data: { links: [...existingLinks, { title, url }] } });
      toastRef.current.show({ severity: 'success', summary: 'Success', detail: 'Link added', life: 3000 });
      onHide();
    } catch (error) {
      toastRef.current.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to add link', life: 3000 });
    }
  };

  return (
    <Dialog visible={visible} onHide={onHide} header="Add Link" modal style={{ width: '480px', maxWidth: '95vw' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div>
          <label htmlFor="link-title" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Title *</label>
          <InputText
            id="link-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., GitHub, Live Demo"
            className="w-full"
          />
        </div>

        <div>
          <label htmlFor="link-url" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>URL *</label>
          <InputText
            id="link-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            className="w-full"
          />
        </div>

        <Flex gap="1rem" justify="flex-end">
          <Button label="Cancel" className="p-button-text p-button-secondary" onClick={onHide} />
          <Button label="Add Link" className="p-button-primary" loading={isPending} onClick={handleSubmit} />
        </Flex>
      </div>
    </Dialog>
  );
}

// ==================== Remove Skill ====================

export function RemoveSkillDialog({ visible, onHide, project, skillToRemove }) {
  const toastRef = useContext(ToastContext);
  const { mutateAsync: patchProject, isPending } = usePatchProject();

  const handleRemove = async () => {
    if (!skillToRemove) return;

    const remainingSkillIds = (project?.skills || [])
      .filter((s) => s.id !== skillToRemove.id)
      .map((s) => s.id);

    try {
      await patchProject({ projectId: project.id, data: { skill_ids: remainingSkillIds } });
      toastRef.current.show({ severity: 'success', summary: 'Removed', detail: `${skillToRemove.name} removed from project`, life: 3000 });
      onHide();
    } catch (error) {
      toastRef.current.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to remove skill', life: 3000 });
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Remove Skill"
      modal
      style={{ width: '400px' }}
    >
      <p>
        Are you sure you want to remove <strong>{skillToRemove?.name}</strong> from this project?
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
        <Button label="Cancel" className="p-button-text p-button-secondary" onClick={onHide} />
        <Button label="Remove" className="p-button-danger" loading={isPending} onClick={handleRemove} />
      </div>
    </Dialog>
  );
}

// ==================== Remove Link ====================

export function RemoveLinkDialog({ visible, onHide, project, linkToRemove }) {
  const toastRef = useContext(ToastContext);
  const { mutateAsync: patchProject, isPending } = usePatchProject();

  const handleRemove = async () => {
    if (!linkToRemove) return;

    const updatedLinks = (project?.links || []).filter((link) => link.url !== linkToRemove.url);

    try {
      await patchProject({ projectId: project.id, data: { links: updatedLinks } });
      toastRef.current.show({ severity: 'success', summary: 'Removed', detail: 'Link removed from project', life: 3000 });
      onHide();
    } catch (error) {
      toastRef.current.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to remove link', life: 3000 });
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Remove Link"
      modal
      style={{ width: '400px' }}
    >
      <p>
        Are you sure you want to remove <strong>{linkToRemove?.title}</strong>?
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
        <Button label="Cancel" className="p-button-text p-button-secondary" onClick={onHide} />
        <Button label="Remove" className="p-button-danger" loading={isPending} onClick={handleRemove} />
      </div>
    </Dialog>
  );
}
